import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type Notification, NotificationType } from '@/entities/notification';
import {
    setPinnedUniqueId,
    usePatternLiveFilters,
} from '@/entities/pattern-live';

import {
    getPatternStudioLivePath,
    getProfileManagementPath,
} from '@/shared/constants/router';
import { useAppDispatch } from '@/shared/lib/store';
import { Modal } from '@/shared/ui/modal';

import {
    useIsPatternLive,
    useNotificationContent,
    useNotificationDate,
    useNotificationTitle,
} from '../../lib/use-notification-formater';

import cls from './notification-modal.module.css';

interface NotificationModalProps {
    notification: Notification | null;
    isOpen: boolean;
    onClose: () => void;
}

export const NotificationModal = memo((props: NotificationModalProps) => {
    const { notification, isOpen, onClose } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const title = useNotificationTitle(notification);
    const content = useNotificationContent(notification);
    const formattedDate = useNotificationDate(notification);
    const isPatternLive = useIsPatternLive(notification);
    const { updateFilters, clearFilters } = usePatternLiveFilters();

    const handleAction = useCallback(() => {
        switch (notification?.tipo) {
            case NotificationType.PATTERN_ALERT: {
                // Build URL with filters from pattern alert using nuqs format
                const params = new URLSearchParams();

                if (notification.messaggio.pattern_unique_id) {
                    params.set(
                        'unique_id',
                        notification.messaggio.pattern_unique_id,
                    );

                    // Clear first, then set to force re-trigger even if same pattern
                    dispatch(setPinnedUniqueId(null));
                    // Use setTimeout to ensure the clear happens first
                    setTimeout(() => {
                        dispatch(
                            setPinnedUniqueId(
                                notification.messaggio.pattern_unique_id,
                            ),
                        );
                    }, 0);
                }

                clearFilters();
                updateFilters({
                    unique_id: notification.messaggio.pattern_unique_id,
                }); // Reset filters before applying new ones

                // Navigate to pattern live with filters
                navigate(`${getPatternStudioLivePath()}?${params.toString()}`);

                break;
            }
            case NotificationType.SUBSCRIPTION_WARNING:
                navigate(getProfileManagementPath());

                break;
            default:
                break;
        }

        onClose();
    }, [
        clearFilters,
        dispatch,
        navigate,
        notification,
        onClose,
        updateFilters,
    ]);

    if (!notification) return null;

    let primaryActionLabel = t(`notification.actions.${notification.tipo}`);

    if (notification.tipo === NotificationType.PATTERN_ALERT) {
        primaryActionLabel = isPatternLive
            ? t('notification.actions.pattern_alert')
            : t('notification.actions.pattern_alert_expired');
    }

    const isPrimaryActionDisabled =
        notification.tipo === NotificationType.PATTERN_ALERT && !isPatternLive;

    return (
        <Modal
            content={
                <>
                    <div className={cls.timestamp}>{formattedDate}</div>
                    <div className={cls.content}>{content}</div>
                </>
            }
            isOpen={isOpen}
            onClose={() => onClose()}
            primaryAction={{
                callback: handleAction,
                disabled: isPrimaryActionDisabled,
                label: primaryActionLabel,
                variant: isPrimaryActionDisabled ? 'ghost' : 'primary',
            }}
            secondaryAction={{
                callback: onClose,
                isLoading: false,
                label: t('Close'),
            }}
            title={title}
        />
    );
});

NotificationModal.displayName = 'NotificationModal';
